/**
 * Utility functions for credit management
 */

/**
 * Calculate the minimum credit cost for a job
 * This is the base cost without considering data range or GPU usage
 * @returns {number} The minimum credit cost (1)
 */
export function getMinimumJobCreditCost() {
  return 1; // Base cost for any job
}

/**
 * Calculate the estimated credit cost for a job based on parameters
 * @param {Object} params - Job parameters
 * @returns {number} The estimated credit cost
 */
export function calculateEstimatedJobCost(params = {}) {
  if (!params) return getMinimumJobCreditCost();

  // Extract relevant parameters
  const startDate = params.start_date
    ? new Date(params.start_date)
    : new Date();
  const endDate = params.end_date ? new Date(params.end_date) : new Date();
  const useGpu = params.use_gpu || false;

  // Calculate days of data
  const daysOfData =
    Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  // Base cost for any job
  const baseCost = 1;

  // Additional cost based on data range
  const dataCost = Math.max(0, daysOfData - 1) * 0.1; // 0.1 credits per additional day

  // GPU cost if applicable
  const gpuCost = useGpu ? 5 : 0;

  // Calculate total cost (round up to nearest integer)
  const totalCost = Math.ceil(baseCost + dataCost + gpuCost);

  // Ensure minimum cost is 1 credit
  return Math.max(1, totalCost);
}

/**
 * Check if the user has enough credits to run a job
 * @param {Object} creditBalance - User's credit balance from store
 * @param {Object} params - Job parameters (optional)
 * @returns {Object} Result with status and message
 */
// Fallback out-of-credits threshold when the backend hasn't supplied one. The
// authoritative value arrives on the credit-balance payload as `min_required`
// (backend env MIN_CREDIT_BALANCE_TO_ACT, which defaults to the price of one
// backtest - MIN_JOB_CREDIT_COST = 0.5). Below that a run cannot be paid for,
// so the paywall must already be showing.
const DEFAULT_MINIMUM_REQUIRED_BALANCE = 0.5;

export function hasEnoughCredits(creditBalance, params = null) {
  // If no credit balance data, assume not enough credits
  if (!creditBalance) {
    return {
      hasEnough: false,
      message: "Unable to determine credit balance"
    };
  }

  // Authoritative threshold from the backend (GET /credits/balance ->
  // min_required); falls back to the local default if the field is absent.
  const minimumRequired =
    creditBalance.min_required !== undefined &&
    creditBalance.min_required !== null
      ? Number(creditBalance.min_required)
      : DEFAULT_MINIMUM_REQUIRED_BALANCE;

  // Get the estimated cost or calculate based on params
  const estimatedCost = params
    ? calculateEstimatedJobCost(params)
    : getMinimumJobCreditCost();

  // User can act while their balance is at or above the (small) threshold.
  const hasEnough = Number(creditBalance.balance) >= minimumRequired;

  // Return result with appropriate message
  return {
    hasEnough,
    creditCost: estimatedCost,
    minimumRequired,
    availableCredits: creditBalance.balance,
    message: hasEnough
      ? `You have enough credits (${creditBalance.balance} available, minimum ${minimumRequired} required)`
      : `Insufficient credits. Minimum required: ${minimumRequired}, Available: ${creditBalance.balance}`,
    isFreePlan: creditBalance.plan === "free"
  };
}
